import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { catalogue, getVehicle, getValidSelection, getLengthLabel, getRoofLabel, getVehicleLabel, TERMS } from '../data/rentalCatalogue';
import './QuoteForm.css';

const unique = (values) => [...new Set(values)];
const FIELD_LABELS = {
  form: 'Your enquiry', submissionId: 'Your enquiry', customerType: 'Customer type',
  name: 'Full name', email: 'Email address', phone: 'Phone number', company: 'Company name',
  vehicleId: 'Vehicle', length: 'Vehicle length', roof: 'Roof height', trim: 'Trim', service: 'Service', term: 'Preferred term',
  quantity: 'Number of vans', startDate: 'Preferred start date', flexibleDates: 'Flexible dates',
  mileage: 'Estimated mileage', fulfilment: 'Collection or delivery', postcode: 'Delivery postcode', message: 'Your requirements',
};

function createSubmissionId() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function londonDate() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function initialValues(search) {
  const params = new URLSearchParams(search);
  const vehicle = getVehicle(params.get('vehicle'));
  const selection = getValidSelection(vehicle, {
    service: params.get('service'), term: params.get('term'), length: params.get('length'), roof: params.get('roof'), trim: params.get('trim'),
  });
  const specification = params.get('spec');
  return {
    customerType: 'business', name: '', email: '', phone: '', company: '',
    vehicleId: vehicle?.id || '', length: selection.length || '', roof: selection.roof || '', trim: selection.trim || '',
    service: selection.service, term: selection.term, quantity: '1',
    startDate: '', flexibleDates: true, mileage: 'not-sure', fulfilment: 'delivery',
    postcode: '', message: specification ? `Preferred specification: ${specification.slice(0, 500)}. Please confirm the available options.` : '', website: '',
  };
}

function followupEmail(reference, values, vehicle) {
  const term = TERMS[values.service].find((item) => item.value === values.term)?.label || values.term;
  const mileage = {
    'not-sure': 'To discuss', 'under-1000': 'Under 1,000 miles',
    '1000-2000': '1,000–2,000 miles', 'over-2000': 'Over 2,000 miles',
  }[values.mileage];
  const body = [
    `Please help with my saved Vanir enquiry ${reference}.`, '',
    `Name: ${values.name}`, `Customer type: ${values.customerType}`,
    ...(values.company ? [`Company: ${values.company}`] : []),
    `Email: ${values.email}`, `Phone: ${values.phone}`, '',
    `Preferred van: ${vehicle ? getVehicleLabel(vehicle) : 'Help me choose / another Ford van'}`,
    ...(vehicle ? [`Length: ${getLengthLabel(values.length, vehicle)}`, `Roof height: ${getRoofLabel(values.roof, vehicle)}`, `Preferred trim: ${values.trim}`] : []),
    `Service: ${values.service}`, `Preferred term: ${term}`, `Number of vans: ${values.quantity}`,
    ...(values.service === 'hire' ? ['Hire billing: every 28 days; full commitment and final part-period charge to be confirmed.'] : []),
    `Preferred start date: ${values.startDate || 'To discuss'}`, `Flexible dates: ${values.flexibleDates ? 'Yes' : 'No'}`,
    `Estimated use over 28 days: ${mileage}`,
    `Collection or delivery: ${values.fulfilment === 'delivery' ? 'Nationwide delivery' : 'Discuss collection or delivery'}`,
    ...(values.postcode ? [`Delivery postcode: ${values.postcode}`] : []),
    '', 'Additional requirements:', values.message || 'None supplied.', '',
    'Please confirm availability, specification and pricing. This is a quote enquiry, not a booking.',
  ].join('\n');
  return `mailto:rental@vanirmotorgroup.com?subject=${encodeURIComponent(`Vanir quote enquiry ${reference}`)}&body=${encodeURIComponent(body)}`;
}

export function validateQuote(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!/^[+()\d .-]+$/.test(values.phone.trim()) || values.phone.replace(/\D/g, '').length < 7) errors.phone = 'Please enter a valid phone number.';
  const quantity = Number(values.quantity);
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) errors.quantity = 'Please choose between 1 and 100 vans.';
  if (values.startDate) {
    const date = new Date(`${values.startDate}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(values.startDate) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== values.startDate) errors.startDate = 'Please choose a valid start date.';
    else if (values.startDate < londonDate()) errors.startDate = 'Please choose today or a future date.';
  }
  const postcode = values.postcode.replace(/\s/g, '').toUpperCase();
  if (values.fulfilment === 'delivery' && !postcode) errors.postcode = 'Please enter the delivery postcode.';
  else if (postcode && !/^(GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/.test(postcode)) errors.postcode = 'Please enter a valid UK postcode.';
  return errors;
}

function Field({ name, label, optional, hint, error, children, className = '' }) {
  return <div className={`quote-field ${className}`}>
    <label htmlFor={`quote-${name}`}>{label}{optional && <span className="quote-field__optional">Optional</span>}</label>
    {children}
    {hint && <p className="quote-field__hint" id={`quote-${name}-hint`}>{hint}</p>}
    {error && <p className="quote-field__error" id={`quote-${name}-error`}>{error}</p>}
  </div>;
}

function QuoteFormContent({ location }) {
  const [values, setValues] = useState(() => initialValues(location.search));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const lastSubmission = useRef(null);
  const errorSummary = useRef(null);
  const successPanel = useRef(null);
  const vehicle = getVehicle(values.vehicleId);
  const lengths = vehicle ? unique(vehicle.configurations.map((item) => item.length)) : [];
  const roofs = vehicle ? unique(vehicle.configurations.filter((item) => item.length === values.length).map((item) => item.roof)) : [];
  const trims = vehicle ? unique(vehicle.configurations.filter((item) => item.length === values.length && item.roof === values.roof).map((item) => item.trim)) : [];

  useEffect(() => {
    if (Object.keys(errors).length) errorSummary.current?.focus();
  }, [errors]);
  useEffect(() => { if (result) successPanel.current?.focus(); }, [result]);

  function update(name, value) {
    setValues((previous) => {
      const next = { ...previous, [name]: value };
      if (name === 'service') next.term = TERMS[value][0].value;
      if (name === 'customerType' && value === 'personal') next.company = '';
      if (name === 'vehicleId' || name === 'length' || name === 'roof') {
        const selected = getVehicle(next.vehicleId);
        const selection = getValidSelection(selected, next);
        next.length = selection.length || '';
        next.roof = selection.roof || '';
        next.trim = selection.trim || '';
      }
      return next;
    });
    if (errors[name]) setErrors((previous) => { const next = { ...previous }; delete next[name]; return next; });
  }

  function fieldProps(name, hint = false) {
    return {
      id: `quote-${name}`, name, value: values[name],
      onChange: (event) => update(name, event.target.value),
      'aria-invalid': errors[name] ? 'true' : undefined,
      'aria-describedby': [hint && `quote-${name}-hint`, errors[name] && `quote-${name}-error`].filter(Boolean).join(' ') || undefined,
    };
  }

  async function submit(event) {
    event.preventDefault();
    if (isSubmitting) return;
    const validationErrors = validateQuote(values);
    if (Object.keys(validationErrors).length) { setErrors(validationErrors); return; }
    setErrors({});
    setIsSubmitting(true);
    const request = {
      ...values, quantity: Number(values.quantity), schemaVersion: 1,
      sourcePath: `${location.pathname}${location.search}`.slice(0, 300),
    };
    const fingerprint = JSON.stringify(request);
    try {
      if (!lastSubmission.current || lastSubmission.current.fingerprint !== fingerprint) {
        lastSubmission.current = { fingerprint, id: createSubmissionId() };
      }
      const response = await fetch('/api/rental-enquiries', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...request, submissionId: lastSubmission.current.id }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const nextErrors = { form: data?.message || 'We could not save your enquiry. Please try again or call the team.' };
        if (data?.errors && typeof data.errors === 'object') {
          Object.entries(data.errors).forEach(([key, value]) => {
            if (FIELD_LABELS[key] && typeof value === 'string') nextErrors[key] = value;
          });
        }
        setErrors(nextErrors);
      } else if (typeof data?.reference === 'string' && /^VMG-[A-Z0-9-]+$/.test(data.reference)) {
        setResult(data);
      } else {
        setErrors({ form: 'We could not confirm that your enquiry was saved. Please try again or call the team.' });
      }
    } catch (error) {
      setErrors({ form: 'We could not connect to the quote service. Your details are still here. Please try again or call 0151 673 1916.' });
    } finally { setIsSubmitting(false); }
  }

  return <main className="quote-page">
    <section className="quote-intro rental-container">
      <p className="rental-eyebrow">Your next van starts here</p>
      <h1>Let’s find your next van.</h1>
      <p>Tell us what you need. We’ll confirm availability, the right specification and a quote built around your plans.</p>
    </section>
    <div className="rental-container quote-layout">
      {result ? <section className="quote-success quote-card" ref={successPanel} tabIndex="-1" aria-labelledby="quote-success-heading">
        <span className="quote-success__icon" aria-hidden="true">✓</span>
        <p className="rental-eyebrow">{result.notificationStatus === 'disabled' ? 'Staging preview' : 'Enquiry received'}</p>
        <h2 id="quote-success-heading">{result.notificationStatus === 'disabled' ? 'Staging enquiry saved.' : 'Your enquiry has been saved.'}</h2>
        <p className="quote-success__reference">Your reference <strong>{result.reference}</strong></p>
        <p>{result.notificationStatus === 'disabled'
          ? 'Your request is stored for this staging preview. Team notifications are disabled; no email or booking has been sent.'
          : result.notificationStatus === 'sent'
            ? 'The team has been notified and will confirm availability, specification and pricing with you.'
            : 'Team notification is pending. Please email this enquiry or call 0151 673 1916, quoting your saved reference.'}</p>
        {result.notificationStatus !== 'sent' && result.notificationStatus !== 'disabled' && <div className="quote-manual-followup">
          <a className="rental-button" href={followupEmail(result.reference, values, vehicle)}>Email this enquiry <span aria-hidden="true">↗</span></a>
          <a className="quote-manual-followup__call" href="tel:+441516731916">Call 0151 673 1916</a>
          <p>The email button opens your email app with your reference and requirements. Review the message and send it yourself.</p>
        </div>}
        <p>This is a quote enquiry. Your vehicle and agreement are confirmed separately by our team.</p>
        <div className="quote-success__actions"><Link className="rental-button" to="/our-vans">Explore our vans <span aria-hidden="true">↗</span></Link><button className="quote-text-button" type="button" onClick={() => {
          setResult(null); setValues(initialValues(location.search)); lastSubmission.current = null;
        }}>Send another enquiry</button></div>
      </section> : <form className="quote-card quote-form" onSubmit={submit} noValidate aria-label="Request a van quote">
        {Object.keys(errors).length > 0 && <section className="quote-error-summary" ref={errorSummary} tabIndex="-1" role="alert" aria-labelledby="quote-errors-heading">
          <h2 id="quote-errors-heading">Please check your enquiry.</h2>
          <ul>{Object.entries(errors).map(([key, message]) => <li key={key}>{key === 'form' || key === 'submissionId'
            ? message : <a href={`#quote-${key}`} onClick={(event) => { event.preventDefault(); document.getElementById(`quote-${key}`)?.focus(); }}>{FIELD_LABELS[key] || key}: {message}</a>}</li>)}</ul>
        </section>}

        <fieldset disabled={isSubmitting} className="quote-section">
          <legend><span>01</span> Your van</legend>
          <Field name="vehicleId" label="Which van do you have in mind?" error={errors.vehicleId}>
            <select {...fieldProps('vehicleId')}><option value="">Help me choose / another Ford van</option>{catalogue.map((item) => <option key={item.id} value={item.id}>{getVehicleLabel(item)}</option>)}</select>
          </Field>
          {vehicle && <>
            <div className="quote-field-row">
              <Field name="length" label="Vehicle length" error={errors.length}><select {...fieldProps('length')}>{lengths.map((length) => <option key={length} value={length}>{getLengthLabel(length, vehicle)}</option>)}</select></Field>
              <Field name="roof" label="Roof height" error={errors.roof}>{roofs.length > 1
                ? <select {...fieldProps('roof')}>{roofs.map((roof) => <option key={roof} value={roof}>{getRoofLabel(roof, vehicle)}</option>)}</select>
                : <input {...fieldProps('roof')} value={getRoofLabel(values.roof, vehicle)} readOnly />}</Field>
            </div>
            <Field name="trim" label="Preferred trim" error={errors.trim}><select {...fieldProps('trim')}>{trims.map((trim) => <option key={trim}>{trim}</option>)}</select></Field>
          </>}
          <div className="quote-field"><span className="quote-group-label" id="quote-service-label">What works for you?</span><div className="quote-choice-grid" role="group" aria-labelledby="quote-service-label">
            {[['rental', 'Rental', 'Daily, weekly or 28 days'], ['hire', 'Hire', '3, 6, 9 or 12 months']].map(([value, label, description]) => <label className={`quote-choice ${values.service === value ? 'is-selected' : ''}`} key={value}><input type="radio" name="service" value={value} checked={values.service === value} onChange={() => update('service', value)} /><span><strong>{label}</strong><small>{description}</small></span></label>)}
          </div></div>
          <div className="quote-field-row">
            <Field name="term" label="Preferred term" error={errors.term}><select {...fieldProps('term')}>{TERMS[values.service].map((term) => <option key={term.value} value={term.value}>{term.label}</option>)}</select></Field>
            <Field name="quantity" label="Number of vans" error={errors.quantity}><input {...fieldProps('quantity')} type="number" min="1" max="100" step="1" inputMode="numeric" required /></Field>
          </div>
          <p className="quote-billing-note">{values.service === 'hire'
            ? 'Hire is billed every 28 days. Your quote will explain the full agreement commitment and any final part-period charge.'
            : 'Daily, weekly and 28-day rental. Extensions can be discussed with our team, subject to availability and agreement.'}</p>
        </fieldset>

        <fieldset disabled={isSubmitting} className="quote-section">
          <legend><span>02</span> Your plans</legend>
          <div className="quote-field-row">
            <Field name="startDate" label="Preferred start date" optional error={errors.startDate}><input {...fieldProps('startDate')} type="date" min={londonDate()} /></Field>
            <Field name="mileage" label="Estimated mileage" hint="Your expected use over 28 days. This is a quote estimate, not an included mileage allowance." error={errors.mileage}><select {...fieldProps('mileage', true)}><option value="not-sure">Not sure yet</option><option value="under-1000">Under 1,000 miles</option><option value="1000-2000">1,000–2,000 miles</option><option value="over-2000">Over 2,000 miles</option></select></Field>
          </div>
          <label className="quote-checkbox"><input id="quote-flexibleDates" name="flexibleDates" type="checkbox" checked={values.flexibleDates} onChange={(event) => update('flexibleDates', event.target.checked)} />My dates are flexible / I’m still planning</label>
          <div className="quote-field-row">
            <Field name="fulfilment" label="Collection or delivery" error={errors.fulfilment}><select {...fieldProps('fulfilment')}><option value="delivery">Nationwide delivery</option><option value="discuss">Discuss collection or delivery</option></select></Field>
            <Field name="postcode" label="Delivery postcode" optional={values.fulfilment !== 'delivery'} error={errors.postcode}><input {...fieldProps('postcode')} autoComplete="shipping postal-code" maxLength="12" placeholder="e.g. L31 8AW" required={values.fulfilment === 'delivery'} /></Field>
          </div>
          <Field name="message" label="Anything else we should know?" optional hint="Tell us about your work, preferred engine or gearbox, seating, equipment or any other Ford van you need." error={errors.message}><textarea {...fieldProps('message', true)} rows="4" maxLength="3000" placeholder="A little about the job and the van you need…" /></Field>
        </fieldset>

        <fieldset disabled={isSubmitting} className="quote-section quote-section--last">
          <legend><span>03</span> Your details</legend>
          <div className="quote-choice-grid quote-customer-type" role="group" aria-label="Customer type">{[['business', 'Business customer'], ['personal', 'Private customer']].map(([value, label]) => <label className={`quote-choice ${values.customerType === value ? 'is-selected' : ''}`} key={value}><input type="radio" name="customerType" value={value} checked={values.customerType === value} onChange={() => update('customerType', value)} /><span><strong>{label}</strong></span></label>)}</div>
          <div className="quote-field-row">
            <Field name="name" label="Full name" error={errors.name}><input {...fieldProps('name')} autoComplete="name" maxLength="120" required /></Field>
            {values.customerType === 'business' && <Field name="company" label="Company name" optional error={errors.company}><input {...fieldProps('company')} autoComplete="organization" maxLength="160" /></Field>}
          </div>
          <div className="quote-field-row">
            <Field name="email" label="Email address" error={errors.email}><input {...fieldProps('email')} type="email" autoComplete="email" maxLength="254" required /></Field>
            <Field name="phone" label="Phone number" error={errors.phone}><input {...fieldProps('phone')} type="tel" autoComplete="tel" maxLength="40" required /></Field>
          </div>
          <div className="quote-honeypot" aria-hidden="true"><label htmlFor="quote-website">Website</label><input id="quote-website" name="website" value={values.website} onChange={(event) => update('website', event.target.value)} tabIndex="-1" autoComplete="off" /></div>
          <p className="quote-privacy">We’ll use these details to respond to your enquiry. Read our <Link to="/privacy">privacy policy</Link>.</p>
          <button className="rental-button quote-submit" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving your enquiry…' : 'Request my quote'}<span aria-hidden="true">↗</span></button>
          <p className="quote-submit-note" aria-live="polite">{isSubmitting ? 'Please wait while your enquiry is saved.' : 'No payment required. Availability and price confirmed by our team.'}</p>
        </fieldset>
      </form>}

      <aside className="quote-sidebar" aria-label="Your quote at a glance">
        {vehicle && <div className="quote-card quote-selection"><img src={vehicle.image} width="900" height="600" alt={`Illustration of the ${vehicle.powertrainLabel.toLowerCase()} ${vehicle.name}`} /><div><p className="rental-eyebrow">Your preferred van</p><h2>{vehicle.name}</h2><p>{vehicle.bodyLabel}</p><p>{vehicle.powertrainLabel} · {getLengthLabel(values.length, vehicle)} · {getRoofLabel(values.roof, vehicle)} · {values.trim}</p><small>{vehicle.imageLabel || 'Illustrative image. Exact specification and availability confirmed with your quote.'}</small></div></div>}
        <div className="quote-card quote-next"><span className="quote-next__symbol" aria-hidden="true">↗</span><h2>Built around your business.</h2><p>One van or a growing fleet. We’ll help you choose a practical fit for the work ahead.</p><ol><li><span>1</span>Tell us what you need.</li><li><span>2</span>We confirm the options and price.</li><li><span>3</span>Agree your van, term and handover.</li></ol></div>
        <div className="quote-location"><p className="rental-eyebrow">Liverpool based. Nationwide reach.</p><h2>Local team.<br />UK-wide delivery.</h2><address>Unit 1A Crystal Trading Estate<br />Old Racecourse Road<br />Liverpool, L31 8AW</address><p>Delivery arrangements and any charges will be included in your quote.</p><a href="tel:+441516731916">0151 673 1916 <span aria-hidden="true">↗</span></a></div>
      </aside>
    </div>
  </main>;
}

export default function QuoteForm() {
  const location = useLocation();
  return <QuoteFormContent key={`${location.pathname}${location.search}`} location={location} />;
}
